import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useT } from '../lib/i18n';
import { PROVINCES } from '../lib/taxService';
import { licenceFieldLabel, getLicenceProvider } from '../lib/licenceProvider';
import { inferProvinceFromAddress, provinceName } from '../lib/postalProvince';
import { LogOut, Upload, X, ImageIcon, FileText, Shield, Users, Truck, UserPlus, Receipt } from 'lucide-react';
import { isStoreApp } from '../lib/platform';
import { toast } from 'sonner';
import { PushToggleCard } from '../components/PushToggleCard';
import { LicenceVerifyButtons } from '../components/LicenceVerifyButtons';
import { DeleteAccountCard } from '../components/DeleteAccountCard';
import { ChangeEmailCard } from '../components/ChangeEmailCard';
import { ShareAppCard } from '../components/ShareAppCard';
import { ContactCard } from '../components/ContactCard';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { TeamMemberView } from '../components/TeamMemberView';
import { AnnualUpgradePopup } from '../components/AnnualUpgradePopup';

export default function Profile() {
  const { user, signOut } = useAuth();
  const { profile, saveProfile, uploadLogo, removeLogo, isMember, ownerCompany, ownerEmail } = useData();
  const { t, lang, setLang } = useT();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', phone: '', email: '', address: '', rbq_number: '',
    business_number: '', gst_hst_number: '', qst_number: '',
    default_hourly_rate: 65, default_margin: 15, province: 'QC', language: 'fr',
  });
  const [saving, setSaving] = useState(false);
  // Track whether the form has already been hydrated from profile.
  // Prevents background profile syncs from wiping what the user is typing.
  const hydratedRef = useRef(false);
  // Track which (address, province) pair we already suggested for, to avoid
  // showing the same toast over and over while the user keeps typing.
  const lastSuggestionRef = useRef({ address: '', from: '', to: '' });

  // Auto-detect province from a Canadian postal code in the address.
  // Shows a non-blocking toast with a one-click "Switch" action.
  useEffect(() => {
    const addr = (form.address || '').trim();
    if (!addr) return;
    const detected = inferProvinceFromAddress(addr);
    if (!detected) return;
    if (detected === (form.province || '').toUpperCase()) return;
    // Skip if we just suggested this exact change.
    const last = lastSuggestionRef.current;
    if (last.address === addr && last.from === form.province && last.to === detected) return;
    lastSuggestionRef.current = { address: addr, from: form.province, to: detected };
    toast(
      lang === 'fr'
        ? `Selon votre code postal, vous semblez être au ${provinceName(detected, 'fr')}.`
        : `Based on your postal code, you appear to be in ${provinceName(detected, 'en')}.`,
      {
        id: `province-suggest-${detected}`,
        duration: 8000,
        action: {
          label: lang === 'fr' ? `Passer en ${detected}` : `Switch to ${detected}`,
          onClick: () => setForm((f) => ({ ...f, province: detected })),
        },
      }
    );
  }, [form.address, form.province, lang]);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (profile) {
      setForm({
        company_name: profile.company_name || '',
        phone: profile.phone || '',
        email: profile.email || user?.email || '',
        address: profile.address || '',
        rbq_number: profile.rbq_number || '',
        business_number: profile.business_number || '',
        gst_hst_number: profile.gst_hst_number || '',
        qst_number: profile.qst_number || '',
        default_hourly_rate: profile.default_hourly_rate ?? 65,
        default_margin: profile.default_margin ?? 15,
        province: profile.province || 'QC',
        language: profile.language || 'fr',
      });
      hydratedRef.current = true;
    } else if (user) {
      setForm(f => ({ ...f, email: user.email }));
      // We DON'T set hydrated yet — let the real profile populate when it arrives.
    }
  }, [profile, user]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveProfile(form);
      if (form.language !== lang) setLang(form.language);
      toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={t('profile.title')}>
      {!isMember && <AnnualUpgradePopup />}
      <h1 className="font-heading text-4xl font-black tracking-tighter mb-2">{t('profile.title')}</h1>
      <p className="text-sm text-gray-500 mb-6">{user?.email}</p>

      {isMember && (
        <TeamMemberView
          ownerCompany={ownerCompany}
          ownerEmail={ownerEmail}
          lang={lang}
          onSignOut={async () => {
            try { await signOut(); } catch (err) { console.warn('Sign out error:', err); }
            window.location.href = '/auth';
          }}
        />
      )}

      {!isMember && (<>
      <div className="card p-4 mb-6">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">
          {lang === 'fr' ? 'Logo de l\'entreprise' : 'Company logo'}
        </div>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl bg-[#FAF7F2] border-2 border-[#E5DCC9] flex items-center justify-center overflow-hidden shrink-0">
            {profile?.logo_url ? (
              <img src={profile.logo_url} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <ImageIcon className="text-gray-400" size={28} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
              className="hidden"
              data-testid="logo-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 2 * 1024 * 1024) {
                  toast.error(lang === 'fr' ? 'Image > 2 Mo' : 'Image > 2 MB');
                  return;
                }
                setUploading(true);
                try {
                  await uploadLogo(file);
                  toast.success(lang === 'fr' ? '✓ Logo téléversé' : '✓ Logo uploaded');
                } catch (err) {
                  toast.error(err.message || 'Upload failed');
                } finally {
                  setUploading(false);
                  if (fileRef.current) fileRef.current.value = '';
                }
              }}
            />
            <button
              type="button"
              data-testid="logo-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-outline gap-2 !min-h-[44px] text-sm w-full"
            >
              <Upload size={16} />
              {uploading
                ? (lang === 'fr' ? 'Téléversement…' : 'Uploading…')
                : (profile?.logo_url
                  ? (lang === 'fr' ? 'Changer' : 'Change')
                  : (lang === 'fr' ? 'Téléverser un logo' : 'Upload a logo'))}
            </button>
            {profile?.logo_url && (
              <button
                type="button"
                data-testid="logo-remove-btn"
                onClick={async () => {
                  await removeLogo();
                  toast.success('✓');
                }}
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-red-700 hover:underline"
              >
                <X size={12} /> {lang === 'fr' ? 'Retirer le logo' : 'Remove logo'}
              </button>
            )}
            <div className="text-[10px] text-gray-500 mt-2 leading-tight">
              {lang === 'fr'
                ? 'PNG, JPG, WEBP ou SVG · max 2 Mo · ratio recommandé 3:1 (ex: 600×200 px)'
                : 'PNG, JPG, WEBP or SVG · max 2 MB · recommended ratio 3:1 (e.g., 600×200 px)'}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={onSave} className="space-y-4">
        <Field label={t('profile.company')}>
          <input data-testid="profile-company" className="field" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
        </Field>
        <Field label={t('profile.phone')}>
          <input data-testid="profile-phone" className="field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label={t('profile.email')}>
          <input data-testid="profile-email" type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={t('profile.address')}>
          <input data-testid="profile-address" className="field" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
        <Field label={licenceFieldLabel(form.province, lang)}>
          <input
            data-testid="profile-rbq"
            className="field"
            placeholder={getLicenceProvider(form.province).placeholder}
            value={form.rbq_number}
            onChange={(e) => setForm({ ...form, rbq_number: e.target.value })}
          />
        </Field>
        <Field label={`${t('profile.business_number')} (${t('common.optional')})`}>
          <input
            data-testid="profile-business-number"
            className="field"
            placeholder="123456789RC0001"
            value={form.business_number}
            onChange={(e) => setForm({ ...form, business_number: e.target.value })}
          />
        </Field>
        <div className={form.province === 'QC' ? 'grid grid-cols-2 gap-3' : ''}>
          <Field label={`${t('profile.gst_hst_number')} (${t('common.optional')})`}>
            <input
              data-testid="profile-gst-hst"
              className="field"
              placeholder="123456789RT0001"
              value={form.gst_hst_number}
              onChange={(e) => setForm({ ...form, gst_hst_number: e.target.value })}
            />
          </Field>
          {form.province === 'QC' && (
            <Field label={`${t('profile.qst_number')} (${t('common.optional')})`}>
              <input
                data-testid="profile-qst"
                className="field"
                placeholder="1234567890TQ0001"
                value={form.qst_number}
                onChange={(e) => setForm({ ...form, qst_number: e.target.value })}
              />
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('profile.rate')}>
            <input data-testid="profile-rate" type="number" className="field" value={form.default_hourly_rate} onChange={(e) => setForm({ ...form, default_hourly_rate: Number(e.target.value) })} />
          </Field>
          <Field label={t('profile.margin')}>
            <input data-testid="profile-margin" type="number" className="field" value={form.default_margin} onChange={(e) => setForm({ ...form, default_margin: Number(e.target.value) })} />
          </Field>
        </div>
        <Field label={t('profile.province')}>
          <select data-testid="profile-province" className="field" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}>
            {PROVINCES.map((p) => {
              const label = `${p.code} — ${p.name}`;
              return <option key={p.code} value={p.code}>{label}</option>;
            })}
          </select>
        </Field>
        <Field label={t('profile.language')}>
          <div className="grid grid-cols-2 gap-2">
            {['fr', 'en'].map((l) => (
              <button
                key={l}
                type="button"
                data-testid={`lang-${l}`}
                onClick={() => setForm({ ...form, language: l })}
                className={`min-h-[52px] rounded-xl border-2 font-bold text-sm ${
                  form.language === l ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white' : 'border-gray-200'
                }`}
              >
                {l === 'fr' ? 'Français' : 'English'}
              </button>
            ))}
          </div>
        </Field>

        <button data-testid="profile-save" type="submit" disabled={saving} className="btn-primary w-full">
          {saving ? t('common.loading') : t('profile.save')}
        </button>
      </form>

      <div className="mt-6">
        <ChangeEmailCard />
      </div>

      <div className="mt-3">
        <PushToggleCard />
      </div>

      <div className="mt-3">
        <LicenceVerifyButtons rbqNumber={profile?.rbq_number} province={form.province} />
      </div>

      <div className="mt-3">
        <SubscriptionCard />
      </div>

      <div className="mt-3">
        <ShareAppCard />
      </div>

      <div className="mt-3">
        <ContactCard />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-2">
        <Link to="/clients" data-testid="link-clients" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <Users size={14} /> {lang === 'fr' ? 'Clients' : 'Clients'}
        </Link>
        <Link to="/invoices" data-testid="link-invoices" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <Receipt size={14} /> {t('nav.invoices')}
        </Link>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Link to="/suppliers" data-testid="link-suppliers" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <Truck size={14} /> {lang === 'fr' ? 'Fournisseurs' : 'Suppliers'}
        </Link>
        <Link to="/team" data-testid="link-team" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <UserPlus size={14} /> {lang === 'fr' ? 'Mon équipe' : 'My team'}
        </Link>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {/* Apple Guideline 3.1.1 — masquer le lien d'abonnement dans les
            apps de magasin (iOS standalone, TWA Play Store). */}
        {!isStoreApp() && (
          <Link to="/pricing" data-testid="link-pricing" className="btn-outline gap-2 !min-h-[44px] !text-xs">
            <FileText size={14} /> {lang === 'fr' ? 'Abonnement' : 'Subscription'}
          </Link>
        )}
        <Link to="/terms" data-testid="link-terms" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <FileText size={14} /> {lang === 'fr' ? 'Conditions' : 'Terms'}
        </Link>
        <Link to="/privacy" data-testid="link-privacy" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <Shield size={14} /> {lang === 'fr' ? 'Privé' : 'Privacy'}
        </Link>
      </div>

      <button
        data-testid="logout-btn"
        onClick={async () => {
          try {
            await signOut();
          } catch (err) {
            console.warn('Sign out error:', err);
          }
          // Hard navigation to /auth to guarantee redirect on all browsers
          // (iOS Safari sometimes doesn't propagate state changes fast enough)
          window.location.href = '/auth';
        }}
        className="btn-outline w-full mt-6 gap-2 !text-red-700 !border-red-300 hover:!bg-red-50"
      >
        <LogOut size={18} /> {t('auth.logout')}
      </button>

      <div className="mt-6">
        <DeleteAccountCard />
      </div>
      </>)}
    </Layout>
  );
}

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}
